import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Mic, ShieldCheck, Square, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';
import { pickMimeType } from '../Meetings/recorderEngine';

// Empreinte vocale volontaire : reconnaître ses prises de parole dans les réunions dont on est participant.
const MIN_SECONDS = 20;
const MAX_SECONDS = 60;
const TEXT = [
  "Bonjour, je travaille chez Innowide et je lis ce texte pour que Deepflow reconnaisse ma voix pendant nos réunions.",
  "Nous accompagnons des projets de maturation technologique, du prototype mécanique jusqu'au logiciel embarqué.",
  "Chaque semaine, nous faisons le point sur les essais, les devis et les prochaines étapes avec nos clients.",
  "Hier, par exemple, nous avons testé un capteur dans une chambre climatique, à quatre-vingt-cinq degrés, pendant plusieurs heures.",
  "Les mesures étaient stables, mais il faudra vérifier l'étanchéité avant la prochaine itération.",
  "Merci à toute l'équipe pour ce travail, et excellente journée à tous !",
];

const VoiceProfileCard = () => {
  const [status, setStatus] = useState(null);
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | recording | sending
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState(null);
  const media = useRef({});

  const load = useCallback(() => apiService.get('/voice-profile').then(setStatus).catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);

  // Calcul de l'empreinte en cours sur le Mac : on suit.
  const pending = status?.enrollment && ['queued', 'processing'].includes(status.enrollment.status);
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [pending, load]);

  const release = () => {
    const { stream, context, frame, timer } = media.current;
    cancelAnimationFrame(frame);
    clearInterval(timer);
    stream?.getTracks().forEach((track) => track.stop());
    context?.close().catch(() => {});
    media.current = {};
  };
  useEffect(() => release, []);

  const send = async (blob, mimeType) => {
    setPhase('sending');
    try {
      const response = await fetch(`${apiService.getBaseUrl()}/voice-profile/sample?consent=true`, {
        method: 'POST', headers: { 'Content-Type': mimeType, ...apiService.getHeaders() }, body: blob,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `Erreur ${response.status}`);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setPhase('idle');
    }
  };

  const start = async () => {
    setError(null);
    const mimeType = pickMimeType();
    if (!mimeType) { setError("Ce navigateur ne sait pas enregistrer l'audio."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      const meter = () => {
        analyser.getByteTimeDomainData(samples);
        let peak = 0;
        for (const s of samples) peak = Math.max(peak, Math.abs(s - 128));
        setLevel(Math.min(1, peak / 64));
        media.current.frame = requestAnimationFrame(meter);
      };
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => { release(); send(new Blob(chunks, { type: mimeType }), mimeType); };
      const startedAt = Date.now();
      const timer = setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        setSeconds(elapsed);
        if (elapsed >= MAX_SECONDS && recorder.state === 'recording') recorder.stop();
      }, 250);
      media.current = { stream, context, recorder, timer };
      recorder.start();
      meter();
      setSeconds(0);
      setPhase('recording');
    } catch {
      setError("Accès au micro refusé : autorise-le dans la barre d'adresse du navigateur.");
    }
  };

  const stop = () => media.current.recorder?.state === 'recording' && media.current.recorder.stop();

  const remove = async () => {
    setError(null);
    try {
      setStatus(await apiService.delete('/voice-profile'));
      setConsent(false);
    } catch (err) {
      setError(err.message);
    }
  };

  if (!status) return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</p>;

  if (phase === 'recording') {
    return (
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">Lis ce texte à voix haute, naturellement</p>
        <div className="space-y-2 rounded-xl bg-slate-50 p-4 text-[15px] leading-relaxed text-slate-800">
          {TEXT.map((line) => <p key={line}>{line}</p>)}
        </div>
        <div className="flex items-center gap-3">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-600" />
          <span className="w-10 font-mono text-sm tabular-nums">{Math.floor(seconds)} s</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-rose-400 transition-[width]" style={{ width: `${Math.round(level * 100)}%` }} />
          </div>
          <button type="button" onClick={stop} disabled={seconds < MIN_SECONDS}
            className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
            <Square className="h-3 w-3 fill-current" /> {seconds < MIN_SECONDS ? `Encore ${Math.ceil(MIN_SECONDS - seconds)} s` : 'Terminer'}
          </button>
        </div>
      </div>
    );
  }

  const failed = status.enrollment?.status === 'failed';
  return (
    <div className="space-y-3 text-sm">
      <p className="text-slate-600">
        Avec ton accord, Deepflow reconnaît ta voix dans les réunions <strong>dont tu es participant</strong> :
        tes prises de parole sont nommées automatiquement dans le transcript et la synthèse.
      </p>
      {status.enrolled && (
        <p className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-emerald-800">
          <CheckCircle2 className="h-4 w-4" />
          Empreinte enregistrée le {new Date(status.created_at).toLocaleDateString('fr-FR')}.
        </p>
      )}
      {(pending || phase === 'sending') && (
        <p className="flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-blue-800">
          <Loader2 className="h-4 w-4 animate-spin" /> Calcul de l'empreinte sur le Mac d'Innowide…
        </p>
      )}
      {failed && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-900">
          L'échantillon n'a pas pu être utilisé ({status.enrollment.error}). Réessaie dans un endroit calme.
        </p>
      )}
      {!pending && phase === 'idle' && (
        <>
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 p-3 text-xs text-slate-700">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
            <span><ShieldCheck className="mr-1 inline h-4 w-4 text-emerald-600" />{status.consent_text}</span>
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={start} disabled={!consent}
              className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
              <Mic className="h-3.5 w-3.5" /> {status.enrolled ? 'Refaire mon empreinte' : 'Enregistrer ma voix (45 s)'}
            </button>
            {(status.enrolled || failed) && (
              <button type="button" onClick={remove}
                className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-slate-500 hover:text-rose-600">
                <Trash2 className="h-3.5 w-3.5" /> Supprimer mon empreinte
              </button>
            )}
          </div>
        </>
      )}
      <p className="text-xs text-slate-400">
        L'enregistrement sert uniquement à calculer l'empreinte, puis il est détruit. L'empreinte reste dans Deepflow
        et n'est envoyée au Mac d'Innowide que pour les réunions auxquelles tu participes. Tu peux la supprimer à tout moment.
      </p>
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
};

export default VoiceProfileCard;
